import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs, getDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useCurrency } from '@/contexts/CurrencyContext';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Users, Calendar, Music, ChevronDown, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/useToast';
import { formatUkDate, formatUkTime } from '@/lib/adminFormat';

export const AllSessionsView = () => {
  const { format } = useCurrency();
  const { toast } = useToast();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('end_time');
  const [sortOrder, setSortOrder] = useState('desc');
  const [artistMap, setArtistMap] = useState({});

  const getSessionTipTotal = (session) => {
    const stored = Number(session.total_tips) || 0;
    const fromRequests = (session.requests || []).reduce((sum, req) => {
      const tip = Number(req.tip) || 0;
      if (tip > 0) return sum + tip;
      if (req.is_tip_only) return sum + (Number(req.amount_paid) || 0);
      return sum;
    }, 0);
    return Math.max(stored, fromRequests);
  };

  const handleDeleteSession = async (sessionId) => {
    if (!sessionId) return;
    if (!window.confirm('Delete this session from history? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'session_history', sessionId));
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      toast({
        title: 'Session deleted',
        description: 'The session was removed from history and stats.',
      });
    } catch (error) {
      console.error('Error deleting session:', error);
      toast({
        title: 'Delete failed',
        description: error.message || 'Could not delete this session.',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    const loadSessions = async () => {
      if (!db) return;
      
      setLoading(true);
      try {
        // Load all sessions
        const sessionsQuery = query(
          collection(db, 'session_history'),
          orderBy('end_time', 'desc'),
          limit(100) // Load last 100 sessions
        );
        const sessionsSnapshot = await getDocs(sessionsQuery);
        const sessionsData = sessionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        console.log('✅ Loaded sessions from session_history:', sessionsData.length);

        // Get unique artist IDs
        const artistIds = [...new Set(sessionsData.map(s => s.user_id))];
        
        // Load artist profiles
        const artistProfiles = {};
        for (const artistId of artistIds) {
          try {
            const profileDoc = await getDoc(doc(db, 'profiles', artistId));
            if (profileDoc.exists()) {
              artistProfiles[artistId] = profileDoc.data();
            }
          } catch (err) {
            console.warn('Error loading artist profile:', err);
          }
        }
        
        setArtistMap(artistProfiles);
        setSessions(sessionsData);
      } catch (error) {
        console.error('❌ Error loading sessions:', error);
        console.error('Error details:', {
          code: error.code,
          message: error.message,
          stack: error.stack
        });
        // Set empty array on error so UI shows "No sessions found" instead of infinite loading
        setSessions([]);
        setArtistMap({});
      } finally {
        setLoading(false);
      }
    };

    loadSessions();
  }, []);

  // Filter and sort sessions
  const filteredSessions = sessions
    .filter(session => {
      if (!searchTerm) return true;
      const artist = artistMap[session.user_id];
      const artistName = artist?.artist_name || artist?.karaoke_name || artist?.email || 'Unknown';
      return artistName.toLowerCase().includes(searchTerm.toLowerCase()) ||
             session.venue?.toLowerCase().includes(searchTerm.toLowerCase());
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      if (sortBy === 'end_time') {
        aVal = a.end_time?.toDate?.() || new Date(0);
        bVal = b.end_time?.toDate?.() || new Date(0);
      } else if (sortBy === 'total_earnings') {
        aVal = a.total_earnings || 0;
        bVal = b.total_earnings || 0;
      } else if (sortBy === 'total_requests') {
        aVal = a.total_requests || 0;
        bVal = b.total_requests || 0;
      } else if (sortBy === 'unique_visitors') {
        aVal = a.unique_visitors || 0;
        bVal = b.unique_visitors || 0;
      } else if (sortBy === 'conversion_rate') {
        aVal = parseFloat(a.conversion_rate) || 0;
        bVal = parseFloat(b.conversion_rate) || 0;
      } else if (sortBy === 'checkouts') {
        aVal = a.checkouts || 0;
        bVal = b.checkouts || 0;
      } else {
        aVal = a[sortBy] || 0;
        bVal = b[sortBy] || 0;
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
        <p className="text-gray-400">Loading sessions...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search and Sort */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          placeholder="Search by artist or venue..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="bg-gray-700 border-gray-600 text-white flex-1"
        />
        <div className="flex gap-2">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-gray-700 border-gray-600 text-white rounded px-3 py-2"
          >
            <option value="end_time">End Time</option>
            <option value="total_earnings">Earnings</option>
            <option value="total_tips">Tips</option>
            <option value="total_requests">Requests</option>
            <option value="duration_minutes">Duration</option>
            <option value="unique_visitors">Unique Visitors</option>
            <option value="conversion_rate">Conversion Rate</option>
            <option value="checkouts">Checkouts</option>
          </select>
          <Button
            variant="outline"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            className="border-gray-600"
          >
            {sortOrder === 'asc' ? '↑' : '↓'}
          </Button>
        </div>
      </div>

      {/* Sessions List */}
      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {filteredSessions.length === 0 ? (
          <div className="text-center py-8 text-gray-400">
            <Calendar className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No sessions found</p>
          </div>
        ) : (
          filteredSessions.map((session) => {
            const artist = artistMap[session.user_id];
            const artistName = artist?.artist_name || artist?.karaoke_name || artist?.email || 'Unknown';
            const endTime = session.end_time?.toDate?.() || new Date(session.end_time);
            const startTime = session.start_time?.toDate?.() || new Date(session.start_time);
            const isKaraokeSession = String(session.session_id || '').startsWith('karaoke_');
            const totalTips = getSessionTipTotal(session);
            
            return (
              <div
                key={session.id}
                className="bg-gray-700 rounded-lg p-4 hover:bg-gray-600 transition-colors"
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Users className="w-4 h-4 text-blue-400" />
                      <span className="font-semibold text-white">{artistName}</span>
                      {session.venue && (
                        <Badge variant="outline" className="border-gray-500 text-gray-300">
                          {session.venue}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm mb-3">
                      <div>
                        <p className="text-gray-400">Date</p>
                        <p className="text-white">{formatUkDate(endTime)}</p>
                        <p className="text-gray-500 text-xs">{formatUkTime(endTime)}</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Duration</p>
                        <p className="text-white">{session.duration_minutes || 0} min</p>
                      </div>
                      <div>
                        <p className="text-gray-400">Requests</p>
                        <p className="text-white">{session.total_requests || 0}</p>
                        <p className="text-gray-500 text-xs">
                          {session.played_requests || 0} {isKaraokeSession ? 'performed' : 'played'}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-400">Earnings</p>
                        <p className="text-green-400 font-semibold">{format(session.total_earnings || 0)}</p>
                        {totalTips > 0 && (
                          <p className="text-gray-500 text-xs">+{format(totalTips)} tips</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Comprehensive Analytics */}
                    <div className="mt-3 pt-3 border-t border-gray-600">
                      <p className="text-gray-400 text-xs mb-2 font-semibold">Analytics</p>
                      {String(session.session_id || '').startsWith('karaoke_') ? (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-xs">
                          <div>
                            <p className="text-gray-500">Views</p>
                            <p className="text-white font-semibold">{session.total_views || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Unique visitors</p>
                            <p className="text-white font-semibold">{session.unique_visitors || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Free song req.</p>
                            <p className="text-slate-200 font-semibold">
                              {session.karaoke_free_requests != null
                                ? session.karaoke_free_requests
                                : Math.max(
                                    0,
                                    (session.total_requests || 0) - (session.karaoke_paid_checkouts ?? session.checkouts ?? 0)
                                  )}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Paid → basket</p>
                            <p className="text-yellow-400 font-semibold">
                              {(session.karaoke_paid_cart ?? session.songs_added_to_cart) || 0}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Paid checkout</p>
                            <p className="text-green-400 font-semibold">
                              {(session.karaoke_paid_checkouts ?? session.checkouts) || 0}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Conversion</p>
                            <p className="text-cyan-300 font-semibold">
                              {session.conversion_rate ? `${parseFloat(session.conversion_rate).toFixed(1)}%` : '0%'}
                            </p>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 text-xs">
                          <div>
                            <p className="text-gray-500">Unique Visitors</p>
                            <p className="text-white font-semibold">{session.unique_visitors || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Total Views</p>
                            <p className="text-white font-semibold">{session.total_views || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Added to Cart</p>
                            <p className="text-yellow-400 font-semibold">{session.songs_added_to_cart || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Checkouts</p>
                            <p className="text-green-400 font-semibold">{session.checkouts || 0}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Tips</p>
                            <p className="text-rose-300 font-semibold">{format(totalTips)}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Conversion Rate</p>
                            <p className="text-blue-400 font-semibold">
                              {session.conversion_rate ? `${parseFloat(session.conversion_rate).toFixed(1)}%` : '0%'}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {Array.isArray(session.requests) && session.requests.length > 0 && (
                      <details className="mt-3 pt-3 border-t border-gray-600 group">
                        <summary className="text-xs font-semibold text-purple-400 cursor-pointer list-none flex items-center hover:text-purple-300">
                          <Music className="w-3 h-3 mr-1" />
                          Itemized Requests ({session.requests.length})
                          <ChevronDown className="w-3 h-3 ml-auto transition-transform group-open:rotate-180" />
                        </summary>
                        <div className="mt-2 space-y-2 max-h-48 overflow-y-auto pr-2">
                          {session.requests.map((req, idx) => {
                            const subArtist = isKaraokeSession
                              ? (req.artist_name || req.original_artist || '')
                              : (req.original_artist || req.artist_name || '');
                            const paid =
                              Number(req.amount_paid) > 0 ||
                              Number(req.total_paid) > 0 ||
                              req.payment_intent_id ||
                              req.transaction_reference ||
                              req.order_reference;
                            const done = isKaraokeSession
                              ? req.status === 'performed'
                              : req.status === 'played';
                            const denied = req.status === 'denied';
                            const removed = req.status === 'removed';
                            return (
                            <div key={req.id || idx} className="bg-gray-800 rounded p-2 text-xs flex justify-between items-center">
                              <div className="truncate pr-2">
                                <p className="text-gray-200 font-medium truncate">{req.song_title}</p>
                                <p className="text-gray-400 truncate">{subArtist || '—'}</p>
                              </div>
                              <div className="flex flex-col items-end shrink-0">
                                {paid ? (
                                  <span className="text-green-400 font-semibold">{format(Number(req.amount_paid) || Number(req.total_paid) || 0)}</span>
                                ) : (
                                  <span className="text-gray-500 italic">Unpaid</span>
                                )}
                                <span className={`mt-0.5 px-1.5 py-0.5 rounded ${
                                  done ? 'bg-green-900/30 text-green-400' :
                                  denied ? 'bg-red-900/30 text-red-400' :
                                  removed ? 'bg-amber-900/30 text-amber-300' :
                                  req.status === 'skipped' ? 'bg-red-900/30 text-red-400' :
                                  'bg-yellow-900/30 text-yellow-400'
                                }`}>
                                  {req.status || 'pending'}
                                </span>
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </details>
                    )}
                    
                    {(session.viewer_count > 0 || session.peak_viewers > 0) && (
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-400">
                        <span>Viewers: {session.viewer_count || session.peak_viewers || 0}</span>
                        {session.peak_viewers > session.viewer_count && (
                          <span>Peak: {session.peak_viewers}</span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      className="border-red-600 text-red-400 hover:bg-red-900/40"
                      onClick={() => handleDeleteSession(session.id)}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
